import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { useLocale } from 'next-intl';
import { ALBUM_LOCALES, DEFAULT_LOCALE } from '@/shared/constants';
import {
  registerUserScopedStore,
  userScopedStorage,
} from '@/shared/lib/user-scoped-storage';

// Album template is the DESIGN axis. `standard` is free; `architect`/`studio`
// are premium (Pro-tier) — selectable only by users who hold the Tokens Pro
// Pass, exactly like the locked named-model row. Whether blueprints are bundled
// is a separate free toggle (`includeBlueprints`) — it replaces the old
// full/short split.
export type AlbumTemplate = 'standard' | 'architect' | 'studio';

// Premium DESIGN templates — gated behind the Tokens Pro Pass (mirrors the
// closed named-model row). Users with Pro access select them freely; users
// without it see a lock badge and a tap routes to the Pro paywall.
export const PREMIUM_ALBUM_TEMPLATES: readonly AlbumTemplate[] = [
  'architect',
  'studio',
];

export const isPremiumAlbumTemplate = (template: AlbumTemplate): boolean =>
  PREMIUM_ALBUM_TEMPLATES.includes(template);

// Album language codes are validated by ALBUM_LOCALES (shared/constants/locales).
// Using a string keeps this in sync with the centralized list instead of
// duplicating a TS union that drifted out of date.
export type AlbumLanguage = string;

// Measurement system for the album's dimensions and quantities. `auto`
// (default) lets the server decide and is NOT sent on the wire — the
// `measurement_system` field is omitted from the generate request. Metric and
// Imperial are sent as-is (`nullable|in:metric,imperial` server-side).
// Mirrors Apple's AlbumMeasurementSystem.
export type AlbumMeasurementSystem = 'auto' | 'metric' | 'imperial';

interface AlbumSettings {
  selectedTemplate: AlbumTemplate;
  // Whether to include the facade projections (blueprint drawings) in the
  // album. The preparation guide, safety requirements and building regulations
  // each have their own toggle below. Free for everyone — replaces the old
  // `template = full/short` gate. Default ON.
  includeBlueprints: boolean;
  // Materials and elements section. Default ON.
  includeMaterials: boolean;
  // Preparation guide section. Default ON.
  includePreparationGuide: boolean;
  // Safety requirements section. Default ON.
  includeSafetyRequirements: boolean;
  // Building regulations section. Default ON.
  includeBuildingRegulations: boolean;
  // Measurement system for the album content — free for everyone. `auto`
  // (default) lets the server pick; persisted states saved before this field
  // existed hydrate to `auto` via the `merge` defaults layering below.
  measurementSystem: AlbumMeasurementSystem;
  selectedLanguage: AlbumLanguage;
  requirements: string;
}

interface AlbumSettingsState {
  // Settings per concept
  conceptSettings: Record<string, AlbumSettings>;

  // Default settings for new concepts
  defaultSettings: AlbumSettings;

  // Last language the user explicitly picked anywhere. Acts as the seed for
  // every new concept so the choice persists across the rest of the app
  // until the user picks something else. `null` = user has not picked yet,
  // hook layer falls back to UI locale → 'en'.
  lastSelectedLanguage: string | null;
}

interface AlbumSettingsActions {
  // Get settings for a specific concept (with fallback to defaults)
  getConceptSettings: (conceptId: string) => AlbumSettings;

  // Update a single setting for a concept
  updateConceptSetting: <K extends keyof AlbumSettings>(
    conceptId: string,
    key: K,
    value: AlbumSettings[K]
  ) => void;

  // Update multiple settings for a concept
  updateConceptSettings: (
    conceptId: string,
    settings: Partial<AlbumSettings>
  ) => void;

  // Reset concept settings to defaults
  resetConceptSettings: (conceptId: string) => void;

  // Update default settings
  updateDefaultSettings: (settings: Partial<AlbumSettings>) => void;

  // Clear all settings for a concept
  clearConceptSettings: (conceptId: string) => void;

  // Copy settings from one concept to another (for concept inheritance)
  copyConceptSettings: (fromConceptId: string, toConceptId: string) => void;

  // Update the global "last picked language" seed used for new concepts.
  setLastSelectedLanguage: (language: string) => void;
}

type AlbumSettingsStore = AlbumSettingsState & AlbumSettingsActions;

const defaultAlbumSettings: AlbumSettings = {
  // Only `standard` is selectable; the other templates are "coming soon".
  selectedTemplate: 'standard',
  // All content sections default ON — the user opts OUT of any of them.
  includeBlueprints: true,
  includeMaterials: true,
  includePreparationGuide: true,
  includeSafetyRequirements: true,
  includeBuildingRegulations: true,
  measurementSystem: 'auto',
  // Placeholder only — the hook resolves the real default via
  // `lastSelectedLanguage → UI locale → 'en'` before this value reaches the UI.
  selectedLanguage: 'en',
  requirements: '',
};

// Build the seed used when lazily creating settings for a new concept so the
// per-concept record carries the user's most recent language pick from the
// start. UI-locale fallback is layered on top in the hook.
const buildSeedSettings = (lastSelectedLanguage: string | null): AlbumSettings => ({
  ...defaultAlbumSettings,
  selectedLanguage: lastSelectedLanguage ?? defaultAlbumSettings.selectedLanguage,
});

export const useAlbumSettingsStore = create<AlbumSettingsStore>()(
  persist(
    immer((set, get) => ({
      // Initial state
      conceptSettings: {},
      defaultSettings: defaultAlbumSettings,
      lastSelectedLanguage: null,

      // Get settings for a specific concept
      getConceptSettings: (conceptId) => {
        const state = get();
        return (
          state.conceptSettings[conceptId] ||
          buildSeedSettings(state.lastSelectedLanguage)
        );
      },

      // Update a single setting for a concept
      updateConceptSetting: (conceptId, key, value) => {
        set((state) => {
          // Initialize concept settings if not exists
          if (!state.conceptSettings[conceptId]) {
            state.conceptSettings[conceptId] = buildSeedSettings(
              state.lastSelectedLanguage
            );
          }
          // Update the specific setting
          state.conceptSettings[conceptId][key] = value;
          // Mirror language picks into the global seed so the choice
          // propagates to every other concept opened next.
          if (key === 'selectedLanguage' && typeof value === 'string') {
            state.lastSelectedLanguage = value;
          }
        });
      },

      // Update multiple settings for a concept
      updateConceptSettings: (conceptId, settings) => {
        set((state) => {
          // Initialize concept settings if not exists
          if (!state.conceptSettings[conceptId]) {
            state.conceptSettings[conceptId] = buildSeedSettings(
              state.lastSelectedLanguage
            );
          }
          // Update settings
          Object.assign(state.conceptSettings[conceptId], settings);
          if (typeof settings.selectedLanguage === 'string') {
            state.lastSelectedLanguage = settings.selectedLanguage;
          }
        });
      },

      // Reset concept settings to defaults
      resetConceptSettings: (conceptId) => {
        set((state) => {
          state.conceptSettings[conceptId] = buildSeedSettings(
            state.lastSelectedLanguage
          );
        });
      },

      // Update default settings
      updateDefaultSettings: (settings) => {
        set((state) => {
          Object.assign(state.defaultSettings, settings);
        });
      },

      // Clear all settings for a concept
      clearConceptSettings: (conceptId) => {
        set((state) => {
          delete state.conceptSettings[conceptId];
        });
      },

      // Copy settings from one concept to another
      copyConceptSettings: (fromConceptId, toConceptId) => {
        set((state) => {
          const sourceSettings =
            state.conceptSettings[fromConceptId] ||
            buildSeedSettings(state.lastSelectedLanguage);
          state.conceptSettings[toConceptId] = { ...sourceSettings };
        });
      },

      setLastSelectedLanguage: (language) => {
        set((state) => {
          state.lastSelectedLanguage = language;
        });
      },
    })),
    {
      name: 'album-settings',
      // User-scoped key prefix: see `user-scoped-storage.ts`. Without this,
      // user A's per-concept album settings would be visible to user B on
      // the same device.
      storage: createJSONStorage(() => userScopedStorage()),
      partialize: (state) => ({
        conceptSettings: state.conceptSettings,
        defaultSettings: state.defaultSettings,
        lastSelectedLanguage: state.lastSelectedLanguage,
      }),
      // Layer code defaults under every persisted record so settings added
      // after the state was persisted (e.g. the section toggles, default ON)
      // hydrate with their defaults instead of `undefined`.
      merge: (persisted, current) => {
        const p = (persisted ?? {}) as Partial<AlbumSettingsState>;
        return {
          ...current,
          ...p,
          conceptSettings: Object.fromEntries(
            Object.entries(p.conceptSettings ?? {}).map(([id, s]) => [
              id,
              { ...defaultAlbumSettings, ...s },
            ])
          ),
          defaultSettings: {
            ...defaultAlbumSettings,
            ...(p.defaultSettings ?? {}),
          },
        };
      },
    }
  )
);

// Re-hydrate from the new user's bucket whenever the auth identity flips.
registerUserScopedStore(useAlbumSettingsStore);

// Resolve the language a brand-new concept should default to.
// Priority: user's last explicit pick → current UI locale (if it's a valid
// album language) → English. UI-locale resolution lives in the hook because
// the store has no React/next-intl context.
const isAlbumLanguage = (code: string): boolean =>
  ALBUM_LOCALES.some((l) => l.code === code);

const resolveDefaultAlbumLanguage = (
  lastSelectedLanguage: string | null,
  uiLocale: string
): string => {
  if (lastSelectedLanguage && isAlbumLanguage(lastSelectedLanguage)) {
    return lastSelectedLanguage;
  }
  if (isAlbumLanguage(uiLocale)) {
    return uiLocale;
  }
  return DEFAULT_LOCALE;
};

// Convenience hooks for specific concepts
export const useConceptAlbumSettings = (conceptId: string) => {
  const store = useAlbumSettingsStore();
  const uiLocale = useLocale();

  const stored = store.conceptSettings[conceptId];
  const defaultLanguage = resolveDefaultAlbumLanguage(
    store.lastSelectedLanguage,
    uiLocale
  );
  // For untouched concepts, layer the resolved default language on top of
  // the seed. The seed already carries lastSelectedLanguage when present;
  // this branch only wins when both stored and lastSelectedLanguage are absent
  // and the UI locale itself is a valid album language.
  const settings: AlbumSettings = stored ?? {
    ...defaultAlbumSettings,
    selectedLanguage: defaultLanguage,
  };

  const updateSetting = <K extends keyof AlbumSettings>(
    key: K,
    value: AlbumSettings[K]
  ) => {
    store.updateConceptSetting(conceptId, key, value);
  };

  const updateSettings = (newSettings: Partial<AlbumSettings>) => {
    store.updateConceptSettings(conceptId, newSettings);
  };

  const resetSettings = () => {
    store.resetConceptSettings(conceptId);
  };

  return {
    ...settings,
    updateSetting,
    updateSettings,
    resetSettings,
  };
};
